import type { ErrorEvent, Event, EventHint } from "@sentry/nextjs";

import { RESTRICTED_BROWSER_API_NEEDLES } from "./js-error-noise";
import { eventSearchText, hasKnownHost } from "./sentry-event-signatures";

// Keys that must never reach Sentry. Birth-detail kundali flow + payment +
// AI chat traffic all carry one or more of these. Match is case-insensitive
// and substring-based (e.g. "user_phone", "birthPlace" both hit).
const PII_KEYS = [
  "dob",
  "birth",
  "birthdate",
  "birthplace",
  "birthtime",
  "birth_time",
  "birth_date",
  "birth_place",
  "place",
  "tob",
  "phone",
  "mobile",
  "email",
  "name",
  "fullname",
  "first_name",
  "last_name",
  "password",
  "token",
  "secret",
  "authorization",
  "cookie",
  "razorpay_payment_id",
  "razorpay_signature",
  "card",
  "cvv",
  "upi",
];

const REDACTED = "[redacted]";
const MAX_SENTRY_STRING_LENGTH = 500;
const THIRD_PARTY_ANALYTICS_HOSTS = [
  "us.i.posthog.com",
  "eu.i.posthog.com",
  "app.posthog.com",
  "www.google-analytics.com",
  "region1.google-analytics.com",
  "region1.analytics.google.com",
  "www.googletagmanager.com",
  "www.clarity.ms",
  "c.clarity.ms",
];
const EXTENSION_INJECTED_CSP_HOSTS = ["migaku-public-data.migaku.com", "cdn.scite.ai"];
const LARGE_HTTP_PAYLOAD = "large http payload";

export const NOISY_SENTRY_ERROR_MESSAGES = [
  "Rendered more hooks than during the previous render",
  "Failed to read the 'localStorage' property from 'Window'",
  "Failed to execute 'request' on 'LockManager'",
  "Large HTTP payload",
] as const;

type TransactionEvent = Event & { type: "transaction" };
type ScrubbableEvent = ErrorEvent | TransactionEvent;
type SpanEntry = {
  type?: string;
  data?: Array<{
    op?: string;
    data?: Record<string, unknown>;
  }>;
};

function truncateString(value: string): string {
  return value.length > MAX_SENTRY_STRING_LENGTH ? value.slice(0, MAX_SENTRY_STRING_LENGTH) : value;
}

function shouldRedact(key: string): boolean {
  const k = key.toLowerCase();
  return PII_KEYS.some((p) => k.includes(p));
}

function redactValue(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === "string") return REDACTED;
  if (typeof value === "number" || typeof value === "boolean") return REDACTED;
  if (Array.isArray(value)) return value.map(redactValue);
  if (typeof value === "object") return scrubObject(value as Record<string, unknown>);
  return value;
}

function scrubObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (shouldRedact(key)) {
      out[key] = REDACTED;
      continue;
    }
    if (value && typeof value === "object" && !Array.isArray(value)) {
      out[key] = scrubObject(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      out[key] = value.map(redactValue);
    } else if (typeof value === "string") {
      out[key] = truncateString(value);
    } else {
      out[key] = value;
    }
  }
  return out;
}

function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const u = new URL(url);
    for (const key of Array.from(u.searchParams.keys())) {
      if (shouldRedact(key)) u.searchParams.set(key, REDACTED);
    }
    return u.toString();
  } catch {
    return url;
  }
}

function firstException(event: ErrorEvent): { value: string; type: string } {
  const exception = event.exception?.values?.[0];
  return {
    value: exception?.value ?? "",
    type: exception?.type ?? "",
  };
}

interface MaybeFrame {
  filename?: string | null;
  function?: string | null;
  in_app?: boolean | null;
}

function firstExceptionFrames(event: ErrorEvent): MaybeFrame[] {
  const exception = event.exception?.values?.[0] as
    | { stacktrace?: { frames?: MaybeFrame[] } }
    | undefined;
  return exception?.stacktrace?.frames ?? [];
}

function isExtensionNoise(value: string): boolean {
  return value.includes("extension") || value.includes("Cannot set property ethereum");
}

function isShareNoise(value: string, type: string): boolean {
  return value.includes("Share canceled") || (type === "AbortError" && value.includes("share"));
}

function isRootMinifiedNoise(value: string, type: string, requestUrl: string): boolean {
  return type === "Error" && value === "Yd" && requestUrl === "https://ishvaram.com/";
}

function isMuhuratStackNoise(value: string, type: string, requestUrl: string): boolean {
  return (
    type === "RangeError" &&
    value.includes("Maximum call stack size exceeded") &&
    requestUrl.includes("/muhurat/")
  );
}

// Google iOS-app in-app browser ("GIDmobile" / "GSA" / UA reported as
// `browser: Google`) intermittently blows the JS stack inside its WebView's
// own minified runtime (`_.jj`, `_.Qi`, `_.Ji` frames + `Array.isArray`).
// Reproduces on /ai-astrologer (Sentry 7481298635) and /free-kundali
// (Sentry 7474508625) only inside that wrapper — our same bundle runs fine
// in Safari/Chrome on the same iPhone. This is an environment bug we can't
// fix from inside the page, so drop the noise instead of letting it crowd
// out signal in the issue list.
function isInAppBrowserStackNoise(value: string, type: string, browserName: string): boolean {
  if (type !== "RangeError" || !value.includes("Maximum call stack size exceeded")) {
    return false;
  }
  const b = browserName.toLowerCase();
  return b.startsWith("google") || b.includes("gsa") || b.includes("gidmobile");
}

// Google Translate proxy (`translate.goog`) rewrites the page DOM and
// reinjects React fragments, which causes our reconciler to recurse inside
// its sentinel scan (Sentry 7480308245). Not our bug — Translate wraps every
// node with its own observer.
function isTranslateProxyNoise(requestUrl: string): boolean {
  return requestUrl.includes("translate.goog");
}

// Sentry 7481298635 / WEB-1: Chrome Mobile iOS captured a "Maximum call
// stack size exceeded" RangeError with a single anonymised frame
// (`filename: undefined`, `function: None`). Same pattern surfaces on
// SSR-only pages (e.g. /ai-astrologer) where there is no client zustand
// store to blow up — the recursion is happening inside third-party
// analytics (PostHog session recording, MS Clarity, GA4 measurement
// proxy) and our bundle never appears in the trace. Without an actionable
// frame these are pure noise.
function isUnactionableStackOverflow(event: ErrorEvent): boolean {
  const { type, value } = firstException(event);
  if (type !== "RangeError" || !value.includes("Maximum call stack size exceeded")) {
    return false;
  }
  const frames = firstExceptionFrames(event);
  if (frames.length === 0) return true;
  // Considered actionable only if at least one frame names a real source.
  return !frames.some((f) => {
    const filename = f.filename;
    const fn = f.function;
    return Boolean(
      filename && filename !== "undefined" && fn && fn !== "None" && fn !== "<anonymous>"
    );
  });
}

// Sentry 7484849318 / WEB-B: Android Chrome surfaces "SecurityError: The
// request was denied" with zero stack frames after the user clicks any
// element that triggers navigator.share / clipboard / fullscreen / vibrate
// in a denied permission context. Without any frame the event tells us
// neither which API fired nor where to add a fix, so the only useful
// action is to stop logging it. A SecurityError WITH a stack still flows
// through because that one we can debug.
function isNoStackSecurityError(event: ErrorEvent): boolean {
  const { type } = firstException(event);
  if (type !== "SecurityError") return false;
  return firstExceptionFrames(event).length === 0;
}

// Restricted browser contexts (Safari private frames, embedded previews,
// enterprise policy sandboxes) expose APIs but throw SecurityError when SDKs
// touch storage or the Web Locks API. These events name only framework/vendor
// chunks, have no customer impact, and cannot be fixed in a page component.
function isRestrictedBrowserApiNoise(event: ErrorEvent): boolean {
  const { type, value } = firstException(event);
  if (type !== "SecurityError") return false;
  // Shared with the owned js_error beacon (js-error-noise.ts) so the drop list
  // is single-sourced across both error sinks (rules 23/24 "drifting copies").
  return RESTRICTED_BROWSER_API_NEEDLES.some((needle) => value.includes(needle));
}

function hasOnlyRuntimeFrames(event: ErrorEvent): boolean {
  return !firstExceptionFrames(event).some((f) => isSourceOwnedFrame(f.filename));
}

function isNullStorageRuntimeNoise(event: ErrorEvent): boolean {
  const { type, value } = firstException(event);
  if (type !== "TypeError") return false;
  return (
    /Cannot read properties of null \(reading '(getItem|setItem|removeItem)'\)/.test(value) &&
    hasOnlyRuntimeFrames(event)
  );
}

function isConnectionClosedRuntimeNoise(event: ErrorEvent): boolean {
  const { type, value } = firstException(event);
  return type === "Error" && value === "Connection closed." && hasOnlyRuntimeFrames(event);
}

function isRuntimeOnlyDomApiNoise(event: ErrorEvent): boolean {
  const { type, value } = firstException(event);
  if (type !== "TypeError" || !hasOnlyRuntimeFrames(event)) return false;
  return [
    "Cannot read properties of undefined (reading 'addListener')",
    "Cannot read properties of null (reading 'removeChild')",
  ].some((needle) => value.includes(needle));
}

function isThirdPartyAnalyticsFetchNoise(event: ErrorEvent): boolean {
  const { type, value } = firstException(event);
  const message = `${type} ${value} ${event.message ?? ""}`.toLowerCase();
  const isNetworkFetchFailure =
    type === "TypeError" &&
    (message.includes("failed to fetch") ||
      message.includes("networkerror when attempting to fetch resource") ||
      message.includes("load failed"));

  return isNetworkFetchFailure && hasKnownHost(eventSearchText(event), THIRD_PARTY_ANALYTICS_HOSTS);
}

function cspBlockedUrl(event: ErrorEvent): string {
  const csp = (event as { csp?: { blocked_uri?: string; blockedURL?: string } }).csp;
  return csp?.blocked_uri ?? csp?.blockedURL ?? "";
}

function isCspReport(event: ErrorEvent): boolean {
  const message = `${event.message ?? ""} ${firstException(event).value}`.toLowerCase();
  return Boolean(
    (event as { csp?: unknown }).csp ||
    message.includes("content security policy") ||
    message.includes("refused to load")
  );
}

function isExtensionInjectedCspNoise(event: ErrorEvent): boolean {
  if (!isCspReport(event)) return false;
  const text = `${cspBlockedUrl(event)} ${eventSearchText(event)}`.toLowerCase();
  return (
    text.includes("chrome-extension://") ||
    text.includes("moz-extension://") ||
    text.includes("safari-extension://") ||
    hasKnownHost(text, EXTENSION_INJECTED_CSP_HOSTS)
  );
}

function isSentryPayloadNoise(event: ErrorEvent): boolean {
  return eventSearchText(event).includes(LARGE_HTTP_PAYLOAD);
}

function isLargeHttpPayloadTransaction(event: TransactionEvent): boolean {
  const entries = (event as { entries?: SpanEntry[] }).entries ?? [];
  return entries.some((entry) => {
    if (entry.type !== "spans") return false;
    return (entry.data ?? []).some((span) => {
      if (span.op !== "http.client") return false;
      const length = span.data?.["http.response_content_length"];
      return typeof length === "number" && length >= 300_000;
    });
  });
}

// Sentry 7482804492 / WEB-D: "Rendered more hooks than during the previous
// render" on /hi/free-kundali → /free-kundali?intent=… navigation (3
// events / 0 affected users in 3 days, last seen 2026-05-17). All frames
// land inside React's minified runtime — no actionable in-app frame.
// Reproduces only under fast Hindi→English navigation while a Suspense
// boundary is hydrating, which is a Next.js + React 18 framework race
// rather than a bug we can fix from the page. KundaliFormCard now wraps
// KundaliResult in <ErrorBoundary> so the user sees a recoverable
// fallback the next time it fires — and this filter prevents the noise
// from accumulating in the dashboard.
function isHookCountMismatchNoise(event: ErrorEvent): boolean {
  if (!eventSearchText(event).includes("rendered more hooks than during the previous render")) {
    return false;
  }
  const frames = firstExceptionFrames(event);
  // Only suppress when there's no source-owned frame to debug. Sentry's
  // source-map fallback can add React/Next runtime frames (node_modules,
  // turbopack://, app:///_next/static) after Google Translate mutates the DOM;
  // those are framework frames, not Ishvaram component frames.
  return !frames.some((f) => isSourceOwnedFrame(f.filename));
}

function isSourceOwnedFrame(filename: string | null | undefined): boolean {
  if (!filename || filename === "undefined") return false;
  const normalized = filename.toLowerCase();
  return ![
    "app:///_next/static",
    "blob:app:///",
    "_next/static",
    "node_modules/",
    "turbopack://",
    "next/dist/compiled",
  ].some((runtimePath) => normalized.includes(runtimePath));
}

// Sentry 7485650624 / WEB-C: a Chrome browser extension fires
// UnhandledRejection through our page; the rejection value is a plain
// object `{ code, message, status }` so Sentry can't even extract a title
// ("<unknown>"). The signature is dense `[extensionService]` console
// breadcrumbs that originate inside the extension's content script, not
// our app. Drop anything with that fingerprint to avoid the extension
// owner's bugs counting against our error budget.
function isExtensionBreadcrumbNoise(event: ErrorEvent): boolean {
  const breadcrumbs = event.breadcrumbs ?? [];
  if (breadcrumbs.length === 0) return false;
  // Only inspect the most recent breadcrumbs — older ones can match
  // accidentally and flip a real bug into a drop.
  const recent = breadcrumbs.slice(-20);
  const extensionHits = recent.filter((b) => {
    const msg = (typeof b.message === "string" ? b.message : "").toLowerCase();
    return msg.includes("[extensionservice]");
  }).length;
  return extensionHits >= 2;
}

// Browser-noise filter (extensions, share-cancel, the muhurat infinite-loop
// pattern, Google in-app browser, translate proxy). Lifted from
// instrumentation-client.ts so server + edge get the same drops.
function isBrowserNoise(event: ErrorEvent): boolean {
  const { value, type } = firstException(event);
  const requestUrl = event.request?.url ?? "";
  // The SDK attaches the parsed user-agent tag on the event as `browser` or
  // exposes it under `event.contexts.browser.name`. We try both for
  // resilience across SDK versions.
  const browserName =
    (event.tags as Record<string, string> | undefined)?.browser ??
    (event.contexts?.browser as { name?: string } | undefined)?.name ??
    "";

  return [
    isExtensionNoise(value),
    isShareNoise(value, type),
    isRootMinifiedNoise(value, type, requestUrl),
    isMuhuratStackNoise(value, type, requestUrl),
    isInAppBrowserStackNoise(value, type, browserName),
    isTranslateProxyNoise(requestUrl),
    isUnactionableStackOverflow(event),
    isNoStackSecurityError(event),
    isRestrictedBrowserApiNoise(event),
    isNullStorageRuntimeNoise(event),
    isConnectionClosedRuntimeNoise(event),
    isRuntimeOnlyDomApiNoise(event),
    isThirdPartyAnalyticsFetchNoise(event),
    isExtensionInjectedCspNoise(event),
    isSentryPayloadNoise(event),
    isExtensionBreadcrumbNoise(event),
    isHookCountMismatchNoise(event),
  ].some(Boolean);
}

function scrubRequest(event: ScrubbableEvent): void {
  if (!event.request) return;

  event.request.url = scrubUrl(event.request.url);
  if (event.request.data && typeof event.request.data === "object") {
    event.request.data = scrubObject(event.request.data as Record<string, unknown>);
  }
  if (event.request.headers) {
    event.request.headers = scrubObject(event.request.headers as Record<string, unknown>) as Record<
      string,
      string
    >;
  }
  if (event.request.query_string && typeof event.request.query_string === "string") {
    event.request.query_string =
      scrubUrl(`https://x.invalid/?${event.request.query_string}`)?.split("?")[1] ??
      event.request.query_string;
  }
}

function scrubUser(event: ScrubbableEvent): void {
  if (!event.user) return;

  delete event.user.email;
  delete event.user.username;
  delete event.user.ip_address;
}

function scrubBreadcrumbs(event: ScrubbableEvent): void {
  if (!event.breadcrumbs) return;

  event.breadcrumbs = event.breadcrumbs.map((b) => ({
    ...b,
    data: b.data ? scrubObject(b.data as Record<string, unknown>) : b.data,
    message: b.message && typeof b.message === "string" ? truncateString(b.message) : b.message,
  }));
}

function scrubContexts(event: ScrubbableEvent): void {
  if (!event.contexts) return;

  event.contexts = scrubObject(event.contexts as Record<string, unknown>) as typeof event.contexts;
}

function scrubExtra(event: ScrubbableEvent): void {
  if (!event.extra) return;

  event.extra = scrubObject(event.extra as Record<string, unknown>);
}

export function scrubEvent(event: ErrorEvent, _hint?: EventHint): ErrorEvent | null {
  if (isBrowserNoise(event)) return null;

  scrubRequest(event);
  scrubUser(event);
  scrubBreadcrumbs(event);
  scrubContexts(event);
  scrubExtra(event);

  return event;
}

export function scrubTransaction(
  event: TransactionEvent,
  _hint?: EventHint
): TransactionEvent | null {
  if (isLargeHttpPayloadTransaction(event)) return null;

  scrubRequest(event);
  scrubUser(event);
  scrubBreadcrumbs(event);
  scrubContexts(event);
  scrubExtra(event);

  return event;
}
